import cloneDeep from "lodash/cloneDeep";
import difference from "lodash/difference";
import isEmpty from "lodash/isEmpty";
import merge from "lodash/merge";
import orderBy from "lodash/orderBy";
import uniqBy from "lodash/uniqBy";
import { fetchBookings } from "PFApp/booking/services/api";
import { RowChildrenResponse } from "PFApp/booking/store";
import { CategoryRowItem } from "PFApp/booking/types/row";
import { PER_PAGE_UNPAGINATED } from "PFApp/constants/unpaginated";
import { AccessLevel, BookingCategory, CalendarRange } from "PFTypes";

type ResponseMapper = (response: RowChildrenResponse) => Promise<RowChildrenResponse>;
type AppendCategoryRowsToLastPageParams = {
  profileId: number;
  dateRange: CalendarRange;
  bookingCategories: BookingCategory[];
};
type AppendCategoryRowsToLastPage = (params: AppendCategoryRowsToLastPageParams) => ResponseMapper;

export const appendCategoryRowsToLastPage: AppendCategoryRowsToLastPage =
  ({ profileId, dateRange, bookingCategories }) =>
  async (response) => {
    const { meta, entries } = response;
    if (meta && meta.page !== meta.totalPages && !isEmpty(entries)) {
      return response;
    }

    const otherBookings = await fetchBookings({
      profileId,
      activityId: false,
      dateRange,
      perPage: PER_PAGE_UNPAGINATED
    });

    const categoryRows = getCategoryRowsList(otherBookings?.entries, bookingCategories, profileId);

    return merge(cloneDeep(response), {
      meta: { total: (meta?.total || 0) + categoryRows.length },
      entries: [...response.entries, ...categoryRows]
    });
  };

const mapBookingsToCategoryRows = (
  bookings: { booking_category_id: number }[] | undefined,
  profileId: number
): CategoryRowItem[] => {
  if (!bookings) {
    return [];
  }
  const categoryRows = uniqBy(
    bookings,
    ({ booking_category_id: categoryId }) => categoryId
  ).map<CategoryRowItem>(({ booking_category_id: categoryId }) => ({
    id: `profile_${profileId}_category_${categoryId}`,
    bookingCategoryIds: [categoryId]
  }));

  return orderBy(categoryRows, ({ bookingCategoryIds }) => bookingCategoryIds[0], "desc");
};

const getCategoryRowsList = (
  bookings: { booking_category_id: number }[] | undefined,
  bookingCategories: BookingCategory[],
  profileId: number
) => {
  const categoryRows = mapBookingsToCategoryRows(bookings, profileId);

  const availableBookingCategoryIds = bookingCategories
    .filter(({ bookings_permissions: bookingPermissions }) => bookingPermissions !== AccessLevel.Hidden)
    .map(({ id }) => id);

  // filter out hidden category rows
  const resultCategoryRows = categoryRows.filter(
    ({ bookingCategoryIds }) => difference(bookingCategoryIds, availableBookingCategoryIds).length === 0
  );
  // find hidden category rows
  const hiddenCategoryRows = categoryRows.filter(
    ({ bookingCategoryIds }) => difference(bookingCategoryIds, availableBookingCategoryIds).length !== 0
  );
  if (!isEmpty(hiddenCategoryRows)) {
    resultCategoryRows.push({
      id: `profile_${profileId}_category_hidden`,
      hiddenCategory: true,
      bookingCategoryIds: hiddenCategoryRows.flatMap(({ bookingCategoryIds }) => bookingCategoryIds)
    });
  }
  return resultCategoryRows;
};
